// src/pages/ContactUs.js
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from '../context/UserProvider';
import { useTheme } from '../context/ThemeContext';
import { getApp } from 'firebase/app';
import { getFirestore, updateDoc, doc, serverTimestamp, getDoc, setDoc, deleteField } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

export default function ContactUs() {
  const { uid, ready, settings } = useUser();
  const { theme: th, isForest } = useTheme();
  const { t } = useTranslation();
  const db = React.useMemo(() => getFirestore(getApp()), []);
  const storage = React.useMemo(() => getStorage(getApp()), []);
  const [subject, setSubject] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [files, setFiles] = React.useState([]); // File[]
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');
  const [openCount, setOpenCount] = React.useState(0);
  const [view, setView] = React.useState('create'); // 'create' | 'open' | 'closed'
  const [openTickets, setOpenTickets] = React.useState([]);
  const [closedTickets, setClosedTickets] = React.useState([]);
  const [loadingList, setLoadingList] = React.useState(false);
  const [preview, setPreview] = React.useState(null); // { url, name }
  const [closedCount, setClosedCount] = React.useState(0);
  const [selectedTicket, setSelectedTicket] = React.useState(null); // { id, key, ...payload }
  const [editing, setEditing] = React.useState(false);
  const [editSubject, setEditSubject] = React.useState('');
  const [editMessage, setEditMessage] = React.useState('');

  // Helper to parse ticket date (handles Firestore Timestamp or number)
  const formatTicketDate = (d) => {
    if (!d) return '';
    // Firestore Timestamp has toDate() method
    if (typeof d.toDate === 'function') return d.toDate().toLocaleString();
    // Handle seconds-based timestamp (Firestore Timestamp serialized)
    if (d.seconds) return new Date(d.seconds * 1000).toLocaleString();
    // Already a number (milliseconds) or string
    return new Date(d).toLocaleString();
  };

  // Read current open ticket count (number of entries under ticketsOpen/{uid})
  React.useEffect(() => {
    (async () => {
      if (!uid) { setOpenCount(0); return; }
      try {
        const openRef = doc(db, 'ticketsOpen', uid);
        const snap = await getDoc(openRef);
        const data = snap.exists() ? snap.data() : {};
        const count = Object.keys(data || {}).length;
        setOpenCount(count);
      } catch {
        setOpenCount(0);
      }
    })();
  }, [db, uid]);

  // Closed tickets count
  React.useEffect(() => {
    (async () => {
      if (!uid) { setClosedCount(0); return; }
      try {
        const refC = doc(db, 'ticketsClosed', uid);
        const snap = await getDoc(refC);
        const data = snap.exists() ? snap.data() : {};
        setClosedCount(Object.keys(data || {}).length);
      } catch {
        setClosedCount(0);
      }
    })();
  }, [db, uid]);

  const canCreateMore = openCount < 5;

  function onPickFiles(e) {
    setSuccess('');
    setError('');
    const picked = Array.from(e.target.files || []).filter((f) => /^image\//.test(f.type));
    setFiles((prev) => {
      const merged = [...prev, ...picked];
      if (merged.length > 2) {
        setError(t('contact.maxAttachments'));
      }
      return merged.slice(0, 2);
    });
    // Reset the input value so selecting the same file again triggers change
    e.target.value = '';
  }

  function removeFile(idx) {
    setFiles((arr) => arr.filter((_, i) => i !== idx));
  }

  const [filePreviews, setFilePreviews] = React.useState([]);
  React.useEffect(() => {
    const urls = files.map((f) => ({ name: f.name, url: URL.createObjectURL(f) }));
    setFilePreviews(urls);
    return () => {
      urls.forEach((u) => URL.revokeObjectURL(u.url));
    };
  }, [files]);

  // Convert any picked image to JPEG before upload (keeps original size)
  async function fileToJpegBlob(file) {
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onerror = reject;
        fr.onload = () => resolve(fr.result);
        fr.readAsDataURL(file);
      });
      const img = await new Promise((resolve, reject) => {
        const im = new Image();
        im.onload = () => resolve(im);
        im.onerror = reject;
        im.src = dataUrl;
      });
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
      return blob || file;
    } catch {
      return file;
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!uid) { setError(t('contact.signInRequired')); return; }
    if (!subject.trim() || !message.trim()) { setError(t('contact.subjectMessageRequired')); return; }
    if (!canCreateMore) { setError(t('contact.maxOpenTickets')); return; }
    setSubmitting(true);
    try {
      const ts = Date.now();
      const openRef = doc(db, 'ticketsOpen', uid);
      // base payload per legacy structure
      const payload = {
        date: ts,
        form: {
          title: subject.trim(),
          subject: subject.trim(),
          description: message.trim(),
          image: '',
          filePath: '',
          inProgress: false,
        },
        user: {
          email: settings?.email || '',
          firstName: settings?.firstName || '',
          lastName: settings?.lastName || '',
          phoneNumber: settings?.phoneNumber || '',
        },
      };
      await setDoc(openRef, { [ts]: payload }, { merge: true });

      // Upload attachments (if any) to /tickets/{uid}/{timestamp[ -2]}.jpg
      const uploaded = [];
      for (let i = 0; i < files.length; i++) {
        const src = files[i];
        try {
          const name = i === 0 ? `${ts}.jpg` : `${ts}-${i + 1}.jpg`;
          const path = `tickets/${uid}/${name}`;
          const storageRef = ref(storage, path);
          const jpegBlob = await fileToJpegBlob(src);
          await uploadBytes(storageRef, jpegBlob, { contentType: 'image/jpeg' });
          const url = await getDownloadURL(storageRef);
          uploaded.push({ url, path, name, size: jpegBlob.size || src.size, type: 'image/jpeg' });
        } catch (e) {
          console.error('[ContactUs] Storage upload failed', e);
          setError(e?.message || t('contact.failedToUploadImage'));
        }
      }

      if (uploaded.length > 0) {
        const first = uploaded[0];
        const imagesStr = uploaded.map((u) => u.url).join(',');
        await updateDoc(openRef, {
          [`${ts}.form.image`]: imagesStr,
          [`${ts}.form.filePath`]: first?.path || '',
        });
      }

      setSuccess(t('contact.ticketSubmitted'));
      setSubject('');
      setMessage('');
      setFiles([]);

      // Refresh open count
      try {
        const snap = await getDoc(doc(db, 'ticketsOpen', uid));
        const data = snap.exists() ? snap.data() : {};
        setOpenCount(Object.keys(data || {}).length);
      } catch { }
    } catch (err) {
      setError(err?.message || t('contact.failedToSubmit'));
    } finally {
      setSubmitting(false);
    }
  }

  const fetchTickets = React.useCallback(async (kind) => {
    if (!uid) { if (kind === 'open') setOpenTickets([]); else setClosedTickets([]); return; }
    setLoadingList(true);
    try {
      const docRef = doc(db, kind === 'open' ? 'ticketsOpen' : 'ticketsClosed', uid);
      const snap = await getDoc(docRef);
      const data = snap.exists() ? snap.data() : {};
      const items = Object.entries(data)
        .map(([k, v]) => ({ id: `${uid}:${k}`, key: Number(k), ...v }))
        .sort((a, b) => b.date - a.date)
        .slice(0, 50);
      if (kind === 'open') { setOpenTickets(items); setOpenCount(items.length); }
      else {
        setClosedTickets(items); setClosedCount(items.length);
        // Mark all closed tickets as viewed to clear badge
        if (items.length > 0) {
          const viewedMap = {};
          items.forEach(ticket => { viewedMap[String(ticket.key)] = true; });
          try {
            await setDoc(doc(db, 'users', uid), { viewedClosedTickets: viewedMap }, { merge: true });
            // Immediately clear the badge (don't refetch - avoids race condition)
            window.dispatchEvent(new Event('clearClosedTicketsBadge'));
          } catch (e) {
            console.error('[Contact] Failed to update viewedClosedTickets:', e);
          }
        }
      }
    } catch {
      if (kind === 'open') setOpenTickets([]); else setClosedTickets([]);
    } finally {
      setLoadingList(false);
    }
  }, [db, uid]);

  async function deleteTicket(id) {
    try {
      // id format: `${uid}:${ts}`; split it
      const [owner, tsStr] = String(id).split(':');
      const ts = tsStr;
      const openRef = doc(db, 'ticketsOpen', owner);
      const snap = await getDoc(openRef);
      if (!snap.exists()) return;
      const entry = snap.data()?.[ts];
      if (!entry) return;
      // Users can only delete their open ticket; staff resolves/ closes elsewhere
      await updateDoc(openRef, { [ts]: deleteField() });
      try { await deleteObject(ref(storage, `tickets/${owner}/${ts}.jpg`)); } catch { }
      try { await deleteObject(ref(storage, `tickets/${owner}/${ts}-2.jpg`)); } catch { }
      // Reflect locally
      setOpenTickets((arr) => arr.filter((ticket) => ticket.id !== id));
      setOpenCount((c) => Math.max(0, c - 1));
      if (selectedTicket?.id === id) setSelectedTicket(null);
    } catch (e) {
      setError(e?.message || t('contact.failedToDelete'));
    }
  }

  async function deleteClosedTicket(id) {
    try {
      const [owner, tsStr] = String(id).split(':');
      const ts = tsStr;
      const closedRef = doc(db, 'ticketsClosed', owner);
      const snap = await getDoc(closedRef);
      if (!snap.exists()) return;
      const entry = snap.data()?.[ts];
      if (!entry) return;
      await updateDoc(closedRef, { [ts]: deleteField() });
      try { await deleteObject(ref(storage, `tickets/${owner}/${ts}.jpg`)); } catch { }
      try { await deleteObject(ref(storage, `tickets/${owner}/${ts}-2.jpg`)); } catch { }
      setClosedTickets((arr) => arr.filter((ticket) => ticket.id !== id));
      setClosedCount((c) => Math.max(0, c - 1));
      if (selectedTicket?.id === id) setSelectedTicket(null);
    } catch (e) {
      setError(e?.message || t('contact.failedToDelete'));
    }
  }

  async function selectTicket(ticket) {
    setSelectedTicket(ticket);
    setEditing(false);
    setEditSubject(ticket?.form?.title || ticket?.form?.subject || '');
    setEditMessage(ticket?.form?.description || '');

    // Mark closed ticket as viewed (only in closed view)
    if (view === 'closed' && ticket?.id && uid) {
      try {
        await setDoc(doc(db, 'users', uid), {
          viewedClosedTickets: { [ticket.id]: true }
        }, { merge: true });
        // Immediately clear the badge (don't refetch - avoids race condition)
        window.dispatchEvent(new Event('clearClosedTicketsBadge'));
      } catch (err) {
        console.error('[ContactUs] Failed to mark ticket as viewed:', err);
      }
    }
  }

  function ticketImages(ticket) {
    const str = typeof ticket?.form?.image === 'string' ? ticket.form.image : '';
    if (str && str.includes(',')) {
      return str.split(',').map((s, i) => ({ url: s.trim(), name: `image_${i + 1}` })).filter((x) => !!x.url);
    }
    if (str) return [{ url: str, name: ticket?.form?.filePath || 'image' }];
    const raw = ticket?.form?.images; // legacy support if present
    if (typeof raw === 'string' && raw.trim()) return raw.split(',').map((s, i) => ({ url: s.trim(), name: `image_${i + 1}` }));
    if (Array.isArray(raw) && raw.length) return raw;
    return [];
  }

  async function saveEdits() {
    if (!selectedTicket) return;
    const [owner, tsStr] = String(selectedTicket.id).split(':');
    const openRef = doc(db, 'ticketsOpen', owner);
    try {
      await updateDoc(openRef, {
        [`${tsStr}.form.title`]: editSubject.trim(),
        [`${tsStr}.form.subject`]: editSubject.trim(),
        [`${tsStr}.form.description`]: editMessage.trim(),
        [`${tsStr}.updatedAt`]: serverTimestamp(),
      });
      setOpenTickets((arr) => arr.map((x) => x.id === selectedTicket.id ? ({
        ...x,
        form: { ...x.form, title: editSubject.trim(), subject: editSubject.trim(), description: editMessage.trim() },
      }) : x));
      setSelectedTicket((x) => x ? ({
        ...x,
        form: { ...x.form, title: editSubject.trim(), subject: editSubject.trim(), description: editMessage.trim() },
      }) : x);
      setEditing(false);
      setSuccess(t('contact.ticketUpdated'));
    } catch (e) {
      setError(e?.message || t('contact.failedToUpdate'));
    }
  }

  React.useEffect(() => {
    if (view === 'open') fetchTickets('open');
    if (view === 'closed') fetchTickets('closed');
  }, [view, fetchTickets]);

  React.useEffect(() => {
    setSelectedTicket(null);
    setEditing(false);
  }, [view]);

  React.useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') setPreview(null);
    }
    if (preview) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [preview]);

  const tabStyle = (active) => ({
    backgroundColor: active ? th.interactive.primary : (isForest ? 'rgba(26,77,64,0.7)' : th.surface.card),
    color: active ? '#ffffff' : (isForest ? '#e8f5e9' : th.text.primary),
    border: active ? 'none' : `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : th.surface.cardBorder}`,
  });

  const cardStyle = {
    backgroundColor: isForest ? '#0f3a30' : th.surface.card,
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.12)' : th.surface.cardBorder}`,
    boxShadow: isForest ? '0 4px 12px rgba(0,0,0,0.3)' : `0 4px 12px ${th.shadow.md}`,
  };

  const inputStyle = {
    backgroundColor: isForest ? 'rgba(15,58,48,0.7)' : th.input.background,
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.08)' : th.input.border}`,
    color: isForest ? '#e8f5e9' : th.text.primary,
  };

  const innerCardStyle = {
    backgroundColor: isForest ? 'rgba(26,77,64,0.5)' : 'rgba(0,0,0,0.03)',
    border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : th.surface.cardBorder}`,
  };

  return (
    <div className="px-0 md:px-0 pt-3" style={{ color: isForest ? '#e8f5e9' : th.text.primary }}>

      <div className="mb-4 flex items-center gap-2">
        {openCount > 0 && (
          <button
            type="button"
            onClick={() => setView('open')}
            className="px-3 py-2 rounded-md font-medium transition-colors"
            style={tabStyle(view === 'open')}
          >
            {t('contact.openTickets', { count: openCount })}
          </button>
        )}
        {closedCount > 0 && (
          <button
            type="button"
            onClick={() => setView('closed')}
            className="px-3 py-2 rounded-md font-medium transition-colors"
            style={tabStyle(view === 'closed')}
          >
            {t('contact.closedTickets', { count: closedCount })}
          </button>
        )}
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setView('create')}
          className="px-3 py-2 rounded-md font-medium transition-colors"
          style={tabStyle(view === 'create')}
        >
          {t('contact.createTicket')}
        </button>
      </div>

      {!ready ? (
        <div style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }}>{t('common.loading')}</div>
      ) : !uid ? (
        <div style={{ color: th.status.error }}>{t('contact.signInRequired')}</div>
      ) : view === 'create' ? (
        <section className="rounded-2xl p-4 md:p-5 shadow-lg" style={cardStyle}>
          <p className="mb-4" style={{ color: isForest ? '#a5d6a7' : th.text.secondary }}>
            {t('contact.createSupportTicket', { max: 5, count: openCount })}
          </p>

          {error && <div className="mb-3 text-sm" style={{ color: th.status.error }}>{error}</div>}
          {success && <div className="mb-3 text-sm" style={{ color: th.status.success }}>{success}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm mb-1" style={{ color: isForest ? '#a5d6a7' : th.text.secondary }}>{t('contact.subject')}</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full rounded-lg p-2.5"
                style={inputStyle}
                placeholder={t('contact.briefSummary')}
                required
              />
            </div>

            <div>
              <label className="block text-sm mb-1" style={{ color: isForest ? '#a5d6a7' : th.text.secondary }}>{t('contact.message')}</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                className="w-full rounded-lg p-2.5"
                style={inputStyle}
                placeholder={t('contact.describeIssue')}
                required
              />
            </div>

            <div>
              <label className="block text-sm mb-1" style={{ color: isForest ? '#a5d6a7' : th.text.secondary }}>{t('contact.attachments')}</label>
              <label
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md cursor-pointer transition-colors"
                style={{
                  backgroundColor: isForest ? 'rgba(26,77,64,0.7)' : th.surface.card,
                  border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : th.surface.cardBorder}`,
                  color: isForest ? '#e8f5e9' : th.text.primary,
                }}
              >
                <span>{t('contact.pickImages')}</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={onPickFiles}
                  className="sr-only"
                />
              </label>
              {files.length > 0 && (
                <span className="ml-2 text-sm" style={{ color: isForest ? '#a5d6a7' : th.text.secondary }}>
                  {files.length} {files.length === 1 ? t('contact.fileSelected') : t('contact.filesSelected')}
                </span>
              )}
              {files.length > 0 && (
                <>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {filePreviews.map((p, i) => (
                      <div key={i} className="relative h-16 w-16">
                        <button
                          type="button"
                          title={p.name}
                          onClick={() => setPreview({ url: p.url, name: p.name })}
                          className="group h-16 w-16 overflow-hidden rounded-md"
                          style={innerCardStyle}
                        >
                          <img src={p.url} alt={p.name} className="h-full w-full object-cover" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          className="absolute -top-2 -right-2 rounded-full px-1 text-xs"
                          style={{
                            backgroundColor: isForest ? 'rgba(15,23,42,0.9)' : th.surface.card,
                            border: `1px solid ${isForest ? 'rgba(255,255,255,0.1)' : th.surface.cardBorder}`,
                            color: isForest ? '#e8f5e9' : th.text.primary,
                          }}
                          aria-label="Remove"
                        >
                          x
                        </button>
                      </div>
                    ))}
                  </div>
                  <ul className="mt-2 space-y-1 text-xs" style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }}>
                    {files.map((f, i) => (
                      <li key={i} className="truncate">{f.name}</li>
                    ))}
                  </ul>
                </>
              )}
              <p className="mt-1 text-xs" style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }}>{t('contact.imagesOnly')}</p>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={!canCreateMore || submitting}
                className="px-4 py-2 rounded-md text-white font-medium transition-colors"
                style={{
                  backgroundColor: (!canCreateMore || submitting) ? (isForest ? '#1a4d40' : '#a5d6a7') : th.interactive.primary,
                  cursor: (!canCreateMore || submitting) ? 'not-allowed' : 'pointer',
                }}
              >
                {submitting ? t('contact.submitting') : t('contact.submitTicket')}
              </button>
            </div>
          </form>
        </section>
      ) : view === 'open' ? (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-5 rounded-2xl p-4 md:p-5 shadow-lg" style={cardStyle}>
            {loadingList ? (
              <div style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }}>{t('contact.loadingTickets')}</div>
            ) : openTickets.length === 0 ? (
              <div style={{ color: isForest ? '#a5d6a7' : th.text.secondary }}>{t('contact.noTickets')}</div>
            ) : (
              <ul className="space-y-3">
                {openTickets.map((ticket) => (
                  <li
                    key={ticket.id}
                    className="rounded-lg p-3 cursor-pointer transition-colors"
                    style={{
                      ...innerCardStyle,
                      borderColor: selectedTicket?.id === ticket.id ? th.interactive.primary : (isForest ? 'rgba(255,255,255,0.1)' : th.surface.cardBorder),
                      boxShadow: selectedTicket?.id === ticket.id ? `0 0 0 2px ${th.interactive.primary}` : 'none',
                    }}
                    onClick={() => selectTicket(ticket)}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-medium" style={{ color: isForest ? '#e8f5e9' : th.text.primary }}>{ticket.form?.title || ticket.form?.subject || t('contact.noSubject')}</div>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); deleteTicket(ticket.id); }}
                        className="px-3 py-1 rounded-md text-white transition-colors"
                        style={{ backgroundColor: th.status.error }}
                      >
                        {t('contact.delete')}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {selectedTicket && (
            <div className="md:col-span-7 rounded-2xl p-4 md:p-5 shadow-lg" style={cardStyle}>
              <div className="rounded-lg p-4" style={innerCardStyle}>
                {!editing ? (
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="text-lg font-semibold" style={{ color: isForest ? '#e8f5e9' : th.text.primary }}>{selectedTicket.form?.title || selectedTicket.form?.subject || t('contact.noSubject')}</div>
                      <div className="text-xs" style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }}>{formatTicketDate(selectedTicket.date)}</div>
                    </div>
                    <button type="button" onClick={() => { setEditing(true); setEditSubject(selectedTicket.form?.title || ''); setEditMessage(selectedTicket.form?.description || ''); }} className="px-3 py-1 rounded-md text-white transition-colors" style={{ backgroundColor: th.interactive.primary }}>{t('contact.edit')}</button>
                  </div>
                ) : (
                  <div className="mb-3 space-y-2">
                    <div>
                      <label className="block text-sm mb-1" style={{ color: isForest ? '#a5d6a7' : th.text.secondary }}>{t('contact.subject')}</label>
                      <input className="w-full rounded-lg p-2.5" style={inputStyle} value={editSubject} onChange={(e) => setEditSubject(e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-sm mb-1" style={{ color: isForest ? '#a5d6a7' : th.text.secondary }}>{t('contact.message')}</label>
                      <textarea className="w-full rounded-lg p-2.5" style={inputStyle} rows={6} value={editMessage} onChange={(e) => setEditMessage(e.target.value)} />
                    </div>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={saveEdits} className="px-3 py-1 rounded-md text-white transition-colors" style={{ backgroundColor: th.interactive.primary }}>{t('contact.save')}</button>
                      <button type="button" onClick={() => { setEditing(false); }} className="px-3 py-1 rounded-md transition-colors" style={tabStyle(false)}>{t('contact.cancel')}</button>
                    </div>
                  </div>
                )}
                {!editing && (
                  <>
                    {selectedTicket.form?.description && (
                      <p className="whitespace-pre-wrap text-sm" style={{ color: isForest ? '#e8f5e9' : th.text.primary }}>{selectedTicket.form.description}</p>
                    )}
                    {ticketImages(selectedTicket).length > 0 && (
                      <div className="mt-3">
                        <div className="text-xs mb-1" style={{ color: isForest ? '#a5d6a7' : th.text.secondary }}>{t('contact.attachmentsLabel')}</div>
                        <div className="flex flex-wrap gap-2">
                          {ticketImages(selectedTicket).map((a, i) => (
                            <button key={i} type="button" title={a.name || `attachment_${i + 1}`} onClick={() => setPreview({ url: a.url, name: a.name || `attachment_${i + 1}` })} className="group relative h-20 w-20 overflow-hidden rounded-md" style={innerCardStyle}>
                              <img src={a.url} alt={a.name || ''} className="h-full w-full object-cover" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-5 rounded-2xl p-4 md:p-5 shadow-lg" style={cardStyle}>
            {loadingList ? (
              <div style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }}>{t('contact.loadingTickets')}</div>
            ) : closedTickets.length === 0 ? (
              <div style={{ color: isForest ? '#a5d6a7' : th.text.secondary }}>{t('contact.noTickets')}</div>
            ) : (
              <ul className="space-y-3">
                {closedTickets.map((ticket) => (
                  <li
                    key={ticket.id}
                    className="rounded-lg p-3 cursor-pointer transition-colors"
                    style={{
                      ...innerCardStyle,
                      borderColor: selectedTicket?.id === ticket.id ? th.interactive.primary : (isForest ? 'rgba(255,255,255,0.1)' : th.surface.cardBorder),
                      boxShadow: selectedTicket?.id === ticket.id ? `0 0 0 2px ${th.interactive.primary}` : 'none',
                    }}
                    onClick={() => selectTicket(ticket)}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-medium" style={{ color: isForest ? '#e8f5e9' : th.text.primary }}>{ticket.form?.title || ticket.form?.subject || t('contact.noSubject')}</div>
                      <button type="button" onClick={(e) => { e.stopPropagation(); deleteClosedTicket(ticket.id); }} className="px-3 py-1 rounded-md text-white transition-colors" style={{ backgroundColor: th.status.error }}>{t('contact.delete')}</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {selectedTicket && (
            <div className="md:col-span-7 rounded-2xl p-4 md:p-5 shadow-lg" style={cardStyle}>
              <div className="rounded-lg p-4" style={innerCardStyle}>
                <div className="mb-3">
                  <div className="text-lg font-semibold" style={{ color: isForest ? '#e8f5e9' : th.text.primary }}>{selectedTicket.form?.title || selectedTicket.form?.subject || t('contact.noSubject')}</div>
                  <div className="text-xs" style={{ color: isForest ? '#a5d6a7' : th.text.tertiary }}>{formatTicketDate(selectedTicket.date)}</div>
                </div>
                {selectedTicket.form?.description && (
                  <p className="whitespace-pre-wrap text-sm" style={{ color: isForest ? '#e8f5e9' : th.text.primary }}>{selectedTicket.form.description}</p>
                )}
                {ticketImages(selectedTicket).length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs mb-1" style={{ color: isForest ? '#a5d6a7' : th.text.secondary }}>{t('contact.attachmentsLabel')}</div>
                    <div className="flex flex-wrap gap-2">
                      {ticketImages(selectedTicket).map((a, i) => (
                        <button key={i} type="button" title={a.name || `attachment_${i + 1}`} onClick={() => setPreview({ url: a.url, name: a.name || `attachment_${i + 1}` })} className="group relative h-20 w-20 overflow-hidden rounded-md" style={innerCardStyle}>
                          <img src={a.url} alt={a.name || ''} className="h-full w-full object-cover" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      {preview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          role="dialog" aria-modal="true"
          onClick={() => setPreview(null)}
        >
          <div className="max-w-4xl max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <img src={preview.url} alt={preview.name} className="max-h-[85vh] w-auto rounded-lg shadow-xl" />
            <div className="mt-2 flex justify-center">
              <button
                type="button"
                onClick={() => setPreview(null)}
                className="px-3 py-1 rounded-md transition-colors"
                style={tabStyle(false)}
              >
                {t('contact.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
